export enum PunchBoundary {
  CheckIn = 1,
  CheckOut = 2,
}

// The shift-punches endpoint names the boundaries, while assign and unassign
// take the numeric enum, so the two forms are mapped rather than mixed.
export type PunchBoundaryKey = "check_in" | "check_out";

export const BOUNDARY_VALUE_BY_KEY: Record<PunchBoundaryKey, PunchBoundary> = {
  check_in: PunchBoundary.CheckIn,
  check_out: PunchBoundary.CheckOut,
};

export const BOUNDARY_LABELS: Record<PunchBoundaryKey, string> = {
  check_in: "Check-in",
  check_out: "Check-out",
};

export enum PunchSource {
  Device = 1,
  Employee = 2,
  Manager = 3,
  Request = 4,
}

export const PUNCH_SOURCE_LABELS: Record<number, string> = {
  [PunchSource.Device]: "Device",
  [PunchSource.Employee]: "Employee",
  [PunchSource.Manager]: "Manager",
  [PunchSource.Request]: "Missing punch request",
};

export interface CandidatePunch {
  id: number;
  sign_in_time: string;
  source: PunchSource;
}

export interface MissingPunchShift {
  shift_id: number;
  employee: string;
  date: string;
  start_time: string;
  type: "missing_check_in" | "missing_check_out";
  check_in: string | null;
  check_out: string | null;
  candidate_punches: CandidatePunch[];
}

export interface MissingPunchParams {
  staff_id?: number;
  start_date?: string;
  end_date?: string;
}

export type ShiftResolutionStatus =
  | "complete"
  | "missing_check_in"
  | "missing_check_out"
  | "absent";

export const RESOLUTION_STATUS_LABELS: Record<string, string> = {
  complete: "Complete",
  missing_check_in: "Missing Check-in",
  missing_check_out: "Missing Check-out",
  absent: "Absent",
};

// A punch can carry more than one assignment, because a boundary punch may be
// shared between two adjacent shifts in different roles.
export interface ShiftPunchAssignment {
  shift_id: number;
  boundary: PunchBoundaryKey;
}

export interface ShiftPunch {
  id: number;
  sign_in_time: string;
  source: PunchSource;
  assignments: ShiftPunchAssignment[];
  // Boundaries this punch may be manually assigned to. The backend owns the
  // rule, so the UI offers exactly what this list contains and nothing else.
  available_for: PunchBoundaryKey[];
}

export interface ShiftBoundaryDetails {
  time: string | null;
  punch_id: number | null;
  source: PunchSource | null;
  // Assigned by hand, so recalculation won't move it, and it can be unassigned.
  manual: boolean;
  // The punch behind this boundary also serves an adjacent shift.
  reused: boolean;
}

// Shift-scoped punch details. Unlike missing-punch this answers for every shift
// state, and returns every punch inside the shift's retrieval window.
export interface ShiftPunchDetails {
  shift_id: number;
  resolution_status: ShiftResolutionStatus;
  check_in: ShiftBoundaryDetails | null;
  check_out: ShiftBoundaryDetails | null;
  punches: ShiftPunch[];
}

export interface ShiftPunchesParams {
  shift_id: number;
}

export interface AssignPunchBody {
  shift_id: number;
  punch_id: number;
  boundary: PunchBoundary;
}

export interface UnassignPunchBody {
  shift_id: number;
  boundary: PunchBoundary;
}

// The recalculate endpoint takes one of three mutually exclusive scopes.
export interface RecalculateShiftBody {
  shift_id: number;
}

// Neither bulk scope carries dates: the backend resolves the range itself,
// from the first day of the month two months back up to the execution date.
export interface RecalculateSelectedStaffBody {
  scope: "selected_staff";
  staff_ids: number[];
}

export interface RecalculateAllBody {
  scope: "all";
}

export type RecalculateBody =
  | RecalculateShiftBody
  | RecalculateSelectedStaffBody
  | RecalculateAllBody;

export interface RecalculateResponse {
  detail?: string;
  message?: string;
  scope?: RecalculateSelectedStaffBody["scope"] | RecalculateAllBody["scope"];
  // The range the backend resolved and actually ran against.
  date_from?: string;
  date_to?: string;
  staff_count?: number;
  shifts_recalculated?: number;
  shifts_resolved?: number;
  shifts_remaining_open?: number;
}
